import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface RouteGuardProps {
  children: React.ReactNode;
  requiredUserType: 'admin' | 'landlord' | 'tenant' | 'service_provider';
  redirectTo?: string;
}

const RouteGuard = ({ children, requiredUserType, redirectTo = '/' }: RouteGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const checkAuthorization = async () => {
      try {
        setLoading(true);

        // للمشرف - فحص جلسة المشرف أولاً
        if (requiredUserType === 'admin') {
          const adminSession = localStorage.getItem('admin_session');
          if (adminSession) {
            if (isMounted) {
              setAuthorized(true);
              setLoading(false);
            }
            return;
          }

          // فحص Supabase auth للمشرفين
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const { data: adminData } = await supabase
              .from('kanfadmin')
              .select('*')
              .eq('email', session.user.email)
              .eq('is_active', true)
              .maybeSingle();

            if (adminData) {
              if (isMounted) {
                setAuthorized(true);
                setLoading(false);
              }
              return;
            }
          }
        }

        // للمالك والباحث وصاحب الخدمة
        if (requiredUserType === 'landlord' || requiredUserType === 'tenant' || requiredUserType === 'service_provider') {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            if (isMounted) {
              navigate('/login');
            }
            return;
          }

          let userData = null;
          
          if (requiredUserType === 'landlord') {
            // السماح بالدخول للملاك بغض النظر عن حالة التفعيل
            // فقط التحقق من وجود السجل في الجدول
            const { data } = await supabase
              .from('landlords')
              .select('*')
              .eq('user_id', session.user.id)
              .maybeSingle();
            userData = data;
          } else if (requiredUserType === 'service_provider') {
            // السماح بالدخول لأصحاب الخدمات بغض النظر عن حالة التفعيل
            const { data } = await supabase
              .from('service_providers')
              .select('*')
              .eq('user_id', session.user.id)
              .maybeSingle();
            userData = data;
          } else {
            // للباحثين - التحقق من النشاط
            const { data } = await supabase
              .from('tenants')
              .select('*')
              .eq('user_id', session.user.id)
              .eq('is_active', true)
              .maybeSingle();
            userData = data;
          }

          if (userData) {
            if (isMounted) {
              setAuthorized(true);
              setLoading(false);
            }
            return;
          }
        }

        // إذا لم يكن مصرحاً له
        if (isMounted) {
          navigate(redirectTo);
        }
      } catch (error) {
        console.error('Authorization check error:', error);
        if (isMounted) {
          navigate(redirectTo);
        }
      }
    };

    checkAuthorization();

    // منع استخدام زر الرجوع
    const preventBack = () => {
      if (authorized) {
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', preventBack);
    window.history.pushState(null, '', window.location.href);

    return () => {
      isMounted = false;
      window.removeEventListener('popstate', preventBack);
    };
  }, [location.pathname, requiredUserType, redirectTo, navigate, authorized]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-gray-600">جاري التحقق من الصلاحيات...</p>
        </div>
      </div>
    );
  }

  return authorized ? <>{children}</> : null;
};

export default RouteGuard;
